import { createGlobalStyle } from 'styled-components';
import { visuallyHiddenStyles } from '@lib/styles';

export const GlobalStyles = createGlobalStyle`
  /*
   * Cascade layer order, lowest priority first. This sheet is mounted in the
   * root layout (content/providers.tsx), so on the site it renders first and
   * establishes the order. The blog sheet repeats the same statement for the
   * cases where it mounts on its own — declaring it twice is idempotent, and
   * whichever renders first must get the order right.
   *
   * site-base  — the element defaults below
   * blog-base  — the blog's prose defaults (@dvd/blog/tokens/global-styles)
   * (unlayered) — component styles, which outrank both
   *
   * See docs/adrs/20260807-blog-css-cascade-layers.md
   */
  @layer site-base, blog-base;

  @property --gradient-base-nw {
    syntax: "<color>";
    inherits: true; initial-value: hsla(0, 0%, 100%, 0); // intentionally transparent 
  }
  @property --gradient-base-ne {
    syntax: "<color>";
    inherits: true;
    initial-value: hsla(0, 0%, 100%, 0); // intentionally transparent 
  }
  @property --gradient-base-se {
    syntax: "<color>";
    inherits: true;
    initial-value: hsla(0, 0%, 100%, 0); // intentionally transparent
  }
  @property --gradient-base-sw {
    syntax: "<color>";
    inherits: true;
    initial-value: hsla(0, 0%, 100%, 0); // intentionally transparent
  }

  /* border-box ftw */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  
  /** 
  * Anything that can be anchored to should have extra scroll margin. [id] and
  * :target: since client-side navigations update the URL via pushState, which
  * never updates the target element. */
  [id], :target {
    scroll-margin-block: 5ex;
  }

  /* The main nav is sticky above this viewport height (its gate and height
     both live in Navigation.tsx). Next.js skips sticky elements when picking
     the scroll target on navigation, so without this offset new-page content
     lands underneath the nav. scroll-padding offsets every scroll-to on the
     root scroller — plain navigations and hash anchors alike.
     See: Next 16.2.9, next/link § Scroll offset with sticky headers. */
  @media screen and (min-height: ${({ theme }) => theme.breakpoints.small}) {
    html {
      scroll-padding-block-start: ${({ theme }) => theme.spacing[3.75]};
    }
  }

  :root {
    --global-bg-color: ${({ theme }) => theme.colors.white};
    --global-secondary-bg-color: ${({ theme }) => theme.colors.blue100};
    --global-color: ${({ theme }) => theme.colors.blue800};
    --global-inactive-color: ${({ theme }) => theme.colors.blue700};
    --global-link-color: ${({ theme }) => theme.colors.blue500};
    --global-hover-color: ${({ theme }) => theme.colors.blue700};
    --global-visited-color: ${({ theme }) => theme.colors.purple700};
    --global-bg-image: none;
    /*
     * Site-wide transition timing. Use the composed pair in the transition
     * shorthand — transition: box-shadow var(--global-motion); — and the
     * duration/easing parts individually where the shorthand won't do.
     *
     * Do not reassign these on a component: they inherit, so an override
     * silently retimes every descendant. A component that needs different
     * timing should declare its own local custom property instead.
     */
    --global-motion-duration: ${({ theme }) => theme.transition.duration};
    --global-motion-easing: ${({ theme }) => theme.transition.easing};
    --global-motion: var(--global-motion-duration) var(--global-motion-easing);
    /* The following two repetitions of the initial-value custom props are for Firefox, until it supports @property */
    --gradient-base-nw: hsla(0, 0%, 100%, 0);
    --gradient-base-se: hsla(0, 0%, 100%, 0);
    --gradient-base-ne: hsla(0, 0%, 100%, 0);
    --gradient-base-sw: hsla(0, 0%, 100%, 0);
  }

  /*
   * The site-wide motion switch: zeroing the duration stops every transition
   * that reads --global-motion. 0.01ms rather than 0s so that transitionend
   * still fires for anything that comes to rely on it.
   *
   * This covers transitions only — @keyframes animations don't read the var
   * and still need their own prefers-reduced-motion gates.
   */
  @media (prefers-reduced-motion: reduce) {
    :root {
      --global-motion-duration: 0.01ms;
    }
  }

  html {
    -moz-text-size-adjust: none;
    -webkit-text-size-adjust: none;
    text-size-adjust: none;

    /* _Accessible_ anchor smooth scrolling, site-wide */
    @media (prefers-reduced-motion: no-preference) {
      scroll-behavior: smooth;
    }
  }

  html, body {
    margin: 0;
    padding: 0;
  }

  body {
    font-family: var(--font-inter);
    font-size:  ${({ theme }) => theme.typography.fontSize.bodyMd};
    font-weight: ${({ theme }) => theme.typography.fontWeight.body};
    line-height: ${({ theme }) => theme.typography.lineHeight.body};

    ${({ theme }) => theme.mq.minWidth.large} {
      font-size:  ${({ theme }) => theme.typography.fontSize.desktop.bodyMd};
    }
    background-color: var(--global-bg-color);
    background-image:
      radial-gradient(
        ellipse 100% 80% at 90% 90%,
        var(--gradient-base-se) 30%,
        transparent
      ),
      radial-gradient(
        ellipse 100% 90% at -10% 0%,
        var(--gradient-base-nw) 40%,
        transparent
      ),
      radial-gradient(
        ellipse at 100% 35%, 
        var(--gradient-base-ne),
        transparent 28%
      ), 
      radial-gradient(
        ellipse at 0% 75%, 
        var(--gradient-base-sw),
        transparent 28%
      ),
      var(--global-bg-image);
    background-repeat: no-repeat, no-repeat, no-repeat, no-repeat, repeat-y;
    background-position: top center;
    color: var(--global-color);
    transition-property: color, --gradient-base-nw, --gradient-base-ne, --gradient-base-se, --gradient-base-sw, background-color;
    transition-duration: 666ms, 666ms, 666ms, 666ms, 666ms, 333ms;
    transition-delay: 222ms, 222ms, 222ms, 222ms, 222ms, 111ms;
    transition-timing-function: ease-in-out;
  }

  /*
   * These two stay OUTSIDE site-base on purpose. At 0,1,1 a[class] outranks a
   * plain component class (0,1,0), so an anchor component that sets font-weight
   * or line-height loses to this rule today — the declaration is there, it just
   * never wins. Layering the rule would reverse that and apply all of them at
   * once, across pricing, navigation, the footer and blog pagination. That is a
   * site-wide typography change unrelated to the blog, so it is deferred rather
   * than made a side effect of this one.
   *
   * The affected components and the path out are in the "Keep a rule unlayered"
   * rule in docs/adrs/20260807-blog-css-cascade-layers.md.
   *
   * Neither collides with the blog sheet: blog only styles a:not([class]),
   * which is disjoint from a[class].
   */
  a[class] {
    font-weight: ${({ theme }) => theme.typography.fontWeight.link};
    line-height: ${({ theme }) => theme.typography.lineHeight.link};
  }

  textarea:not([rows]) {
    min-height: 10em
  }

  @layer site-base {

  h1, h2, h3, h4, h5, h6 {
    text-wrap: balance;
    margin: 0 0 ${({ theme }) => theme.spacing[1.5]};
  }

  h1 {
    font-family: var(--font-walsheim);
    font-size:  ${({ theme }) => theme.typography.fontSize.headingOne};
    font-weight: ${({ theme }) => theme.typography.fontWeight.headingOne};
    letter-spacing: ${({ theme }) => theme.typography.letterSpacing.headingOne};
    line-height: ${({ theme }) => theme.typography.lineHeight.headingOne};

    ${({ theme }) => theme.mq.minWidth.large} {
      font-size:  ${({ theme }) =>
        theme.typography.fontSize.desktop.headingOne};
    }
  }

  h2 {
    font-family: var(--font-walsheim);
    font-size:  ${({ theme }) => theme.typography.fontSize.headingTwo};
    font-weight: ${({ theme }) => theme.typography.fontWeight.headingTwo};
    line-height: ${({ theme }) => theme.typography.lineHeight.headingTwo};

    ${({ theme }) => theme.mq.minWidth.large} {
      font-size:  ${({ theme }) =>
        theme.typography.fontSize.desktop.headingTwo};
    }
  }

  h3 {
    font-family: var(--font-walsheim);
    font-size:  ${({ theme }) => theme.typography.fontSize.headingThree};
    font-weight: ${({ theme }) => theme.typography.fontWeight.headingThree};
    line-height: ${({ theme }) => theme.typography.lineHeight.headingThree};

     ${({ theme }) => theme.mq.minWidth.large} {
      font-size:  ${({ theme }) =>
        theme.typography.fontSize.desktop.headingThree};
    }
  }

  h4 {
    font-family: var(--font-walsheim);
    font-size:  ${({ theme }) => theme.typography.fontSize.headingFour};
    font-weight: ${({ theme }) => theme.typography.fontWeight.headingFour};
    line-height: ${({ theme }) => theme.typography.lineHeight.headingFour};

     ${({ theme }) => theme.mq.minWidth.large} {
      font-size:  ${({ theme }) =>
        theme.typography.fontSize.desktop.headingFour};
    }
  }

  h5 {
    font-family: var(--font-walsheim);
    font-size:  ${({ theme }) => theme.typography.fontSize.headingFive};
    font-weight: ${({ theme }) => theme.typography.fontWeight.headingFive};
    line-height: ${({ theme }) => theme.typography.lineHeight.headingFive};

    ${({ theme }) => theme.mq.minWidth.large} {
      font-size:  ${({ theme }) =>
        theme.typography.fontSize.desktop.headingFive};
    }
  }

  hr {
    color: var(--global-link-color);
    height: 1px;
  }
  h2 + hr {
    margin-block-start: -${({ theme }) => theme.spacing[1]};
  }

  p {
    margin: 0 0 ${({ theme }) => theme.spacing[1.5]};
  }
  /* 48px: Paragraph space between Super Title, H1 and any body style */
  h1 + p,
  p + h1 {
    margin-block-start: ${({ theme }) => theme.spacing[3]};
  }

  a {
    font-size: inherit;
    cursor: pointer;
    text-decoration: none;
  }

  a:not([class]) {
    color: var(--global-link-color);
    text-decoration-skip-ink: auto;
    text-decoration: underline currentColor;
    transition: text-decoration var(--global-motion);

    &:visited {
      color: var(--global-visited-color);
    }
    // https://css-tricks.com/remember-selectors-with-love-and-hate/
    &:hover, &:active {
      text-decoration-color: transparent;
      color: var(--global-hover-color);
    }
    
  }

  /* 48px: Space between any text type with an image in between */
  /* @deprecated
  p + img:not([class]),
  p + picture:not([class]) {
    margin-block-start: ${({ theme }) => theme.spacing[3]};
    margin-block-end: ${({ theme }) => theme.spacing[3]};
  }
  */

  blockquote {
    margin-block-start: 0;
    margin-block-end: 0;
    margin-inline-start: 0;
    margin-inline-end: 0;
  }
  
  cite {
    font-style: normal;
  }

  input,
  button,
  textarea,
  select {
    font: inherit;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    border-spacing: ${({ theme }) => theme.spacing[1]} 0;
    border-radius: ${({ theme }) => theme.radius.sm};
    background-color: ${({ theme }) => theme.colors.white};
    box-shadow: ${({ theme }) => theme.elevation.elevation2()};
  }

  thead {
    width: 100%;
  }

  td {
    font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
    width: 1%;  /* all cells compete equally */
   
    /* Custom line height for table cells */
    line-height: 1.4;
    text-align: start;
    padding: ${({ theme }) => theme.spacing[0.5]};

    &:first-child {
      padding-inline-start: ${({ theme }) => theme.spacing[0.75]};
    }

    ${({ theme }) => theme.mq.minWidth.large} {
      font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodySm};
      padding: ${({ theme }) => theme.spacing[0.75]};

      &:first-child {
        padding-inline-start: ${({ theme }) => theme.spacing[1.25]};
      }
    }
  }

  th {
    font-size: ${({ theme }) => theme.typography.fontSize.headingFive};
    
    /* Custom line height for table cells */
    line-height: 1.2;
    text-align: start;
    padding: ${({ theme }) => theme.spacing[0.5]};

    thead & {
      padding-block-start: ${({ theme }) => theme.spacing[0.75]};
    }
    &:first-child {
      padding-inline-start: ${({ theme }) => theme.spacing[0.75]};
    }

    ${({ theme }) => theme.mq.minWidth.large} {
      font-size: ${({ theme }) => theme.typography.fontSize.desktop.headingFive};
      padding: ${({ theme }) => theme.spacing[0.75]};

      thead & {
        padding-block-start: ${({ theme }) => theme.spacing[1]};
      }
      &:first-child {
        padding-inline-start: ${({ theme }) => theme.spacing[1.25]};
      }
    }

  }

  tr {
    /* with border-collapse, we can stick to just one border side */
    border-bottom: 1px solid ${({ theme }) => theme.colors.blue100};

    /* the last row of the last tbody should not have a border-bottom, to avoid extending outside the table's border-radius */
    tbody:last-of-type &:last-of-type {
      border-bottom: none;
    }
  }

  } /* @layer site-base */

  /* Utility: unlayered so it always wins, whatever it is applied to. */
  .visually-hidden {
    ${visuallyHiddenStyles}
  }
`;
